/**
 * 통합관리자 — 외부 연동
 * ---------------------------------------------------------------------------
 * 두 방향을 한 화면에서 구분해 보여 준다.
 *
 *   나가는 연동 (여기서 관리한다)
 *     가입·결제실패 같은 사건이 생기면 등록한 주소로 우리가 보낸다.
 *
 *   들어오는 연동 (제품 현황에서 관리한다)
 *     외부 시스템이 우리를 부를 때 쓰는 서비스 키다. 이미 그 화면이 있으므로
 *     여기서는 **어디로 가야 하는지만** 알려 준다. 같은 것을 두 화면에서
 *     관리하면 어느 쪽이 진짜인지 모르게 된다.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, useCodes, type CodeItem } from '../../lib/queries';
import { PageHelp, HelpP, HelpH, HelpList } from '../../components/PageHelp';

const { Text, Paragraph } = Typography;

type AuthType = 'NONE' | 'BEARER' | 'HEADER' | 'BASIC' | 'HMAC';

interface Endpoint {
  endpointId: string;
  endpointCode: string;
  endpointName: string;
  description: string | null;
  targetUrl: string;
  httpMethod: string;
  authType: AuthType;
  authHeaderName: string | null;
  hasSecret: boolean;
  events: string[];
  timeoutMs: number;
  maxRetry: number;
  isActive: boolean;
  lastCalledAt: string | null;
  lastStatus: number | null;
  lastError: string | null;
  updatedAt: string;
}

interface Delivery {
  deliveryId: string;
  endpointId: string;
  endpointName: string;
  eventCode: string;
  status: string;
  attempt: number;
  responseStatus: number | null;
  responseBody: string | null;
  errorMessage: string | null;
  queuedAt: string;
  sentAt: string | null;
}

const AUTH_LABEL: Record<AuthType, string> = {
  NONE: '없음',
  BEARER: 'Bearer 토큰',
  HEADER: '헤더에 키',
  BASIC: 'Basic 인증',
  HMAC: 'HMAC 서명',
};

const STATUS_META: Record<string, { text: string; status: 'success' | 'error' | 'processing' | 'default' }> = {
  QUEUED: { text: '대기', status: 'default' },
  SENDING: { text: '보내는 중', status: 'processing' },
  SENT: { text: '성공', status: 'success' },
  FAILED: { text: '실패', status: 'error' },
};

export default function IntegrationPage() {
  const { message, modal } = AntdApp.useApp();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [editing, setEditing] = useState<Endpoint | null>(null);
  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [filterEndpoint, setFilterEndpoint] = useState<string | undefined>();

  const endpoints = useQuery<Endpoint[], NormalizedError>({
    queryKey: ['admin', 'integrations'],
    queryFn: async () => (await apiClient.get<Endpoint[]>('/admin/integrations')).data,
  });

  const deliveries = useQuery<Delivery[], NormalizedError>({
    queryKey: ['admin', 'integrations', 'deliveries', filterEndpoint],
    queryFn: async () =>
      (
        await apiClient.get<Delivery[]>('/admin/integrations/deliveries', {
          params: { endpointId: filterEndpoint, limit: 100 },
        })
      ).data,
    refetchInterval: 30_000,
  });

  // 보낼 수 있는 사건은 공통코드에서 온다. 코드에 박으면 사건을 하나 더할
  // 때마다 화면을 다시 배포해야 한다.
  // 응답은 그룹을 키로 하는 표라서 그 그룹만 꺼내 쓴다.
  const codes = useCodes(['INTEGRATION_EVENT']);
  const eventItems: CodeItem[] = codes.data?.INTEGRATION_EVENT ?? [];

  const refresh = async () => {
    await client.invalidateQueries({ queryKey: ['admin', 'integrations'] });
  };

  const save = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (editing) {
        return (await apiClient.put(`/admin/integrations/${editing.endpointId}`, values)).data;
      }
      return (await apiClient.post('/admin/integrations', values)).data;
    },
    onSuccess: async () => {
      setOpen(false);
      setEditing(null);
      form.resetFields();
      message.success('저장했습니다.');
      await refresh();
    },
    onError: (err) => setFormError(errorText(err as unknown as NormalizedError)),
  });

  const openForm = (row: Endpoint | null) => {
    setEditing(row);
    setFormError(null);
    setOpen(true);
    // destroyOnHidden 이라 열린 뒤에 값을 넣는다.
    setTimeout(() => {
      form.setFieldsValue(
        row
          ? {
              endpointCode: row.endpointCode,
              endpointName: row.endpointName,
              description: row.description,
              targetUrl: row.targetUrl,
              httpMethod: row.httpMethod,
              authType: row.authType,
              authHeaderName: row.authHeaderName,
              secret: '',
              events: row.events,
              timeoutMs: row.timeoutMs,
              maxRetry: row.maxRetry,
              isActive: row.isActive,
            }
          : {
              httpMethod: 'POST',
              authType: 'NONE',
              events: [],
              timeoutMs: 10000,
              maxRetry: 3,
              isActive: true,
            },
      );
    }, 0);
  };

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setFormError(null);
    save.mutate({ ...values, secret: values.secret ? values.secret : undefined });
  };

  const test = async (row: Endpoint) => {
    try {
      const { data } = await apiClient.post<{ ok: boolean; status: number | null; detail: string }>(
        `/admin/integrations/${row.endpointId}/test`,
      );
      if (data.ok) message.success(`연결 확인: ${data.detail}`);
      else modal.error({ title: '연결하지 못했습니다', content: data.detail, width: 520 });
      await refresh();
      await client.invalidateQueries({ queryKey: ['admin', 'integrations', 'deliveries'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const remove = (row: Endpoint) => {
    modal.confirm({
      title: `${row.endpointName} — 삭제`,
      content: '전송 이력도 함께 사라집니다. 잠시 멈추려면 삭제 대신 사용을 끄세요.',
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      async onOk() {
        try {
          await apiClient.delete(`/admin/integrations/${row.endpointId}`);
          message.success('삭제했습니다.');
          await refresh();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          throw err;
        }
      },
    });
  };

  const eventLabel = (code: string) =>
    eventItems.find((e) => e.itemCode === code)?.itemName ?? code;

  const columns: ColumnsType<Endpoint> = [
    {
      title: '연동',
      key: 'name',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <strong>{row.endpointName}</strong>
            {!row.isActive && <Tag>사용 안 함</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.endpointCode}
            {row.description ? ` · ${row.description}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: '보내는 곳',
      key: 'target',
      width: 300,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }} ellipsis>
            {row.httpMethod} {row.targetUrl}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            인증 {AUTH_LABEL[row.authType]}
            {row.authType !== 'NONE' && (row.hasSecret ? ' · 키 설정됨' : ' · 키 없음')}
          </Text>
        </Space>
      ),
    },
    {
      title: '구독 사건',
      key: 'events',
      width: 240,
      render: (_, row) =>
        row.events.length === 0 ? (
          <Tooltip title="사건을 고르지 않으면 아무것도 보내지 않습니다.">
            <Tag color="orange">없음</Tag>
          </Tooltip>
        ) : (
          <Space size={4} wrap>
            {row.events.map((e) => (
              <Tag key={e}>{eventLabel(e)}</Tag>
            ))}
          </Space>
        ),
    },
    {
      title: '최근 결과',
      key: 'last',
      width: 190,
      render: (_, row) =>
        row.lastCalledAt ? (
          <Space direction="vertical" size={0}>
            <Badge
              status={row.lastError ? 'error' : 'success'}
              text={row.lastError ? `실패 ${row.lastStatus ?? ''}` : `HTTP ${row.lastStatus}`}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {new Date(row.lastCalledAt).toLocaleString('ko-KR')}
            </Text>
          </Space>
        ) : (
          <Text type="secondary">보낸 적 없음</Text>
        ),
    },
    {
      title: '관리',
      key: 'actions',
      width: 190,
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="link" icon={<ThunderboltOutlined />} onClick={() => void test(row)}>
            연결 확인
          </Button>
          <Button size="small" type="link" onClick={() => openForm(row)}>
            수정
          </Button>
          <Button size="small" type="text" danger onClick={() => remove(row)}>
            삭제
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="들어오는 연동(외부 시스템이 우리를 부르는 경우)은 「제품 현황」에서 서비스 키로 관리합니다"
        action={
          <Button size="small" onClick={() => navigate('/app/products')}>
            제품 현황으로
          </Button>
        }
      />

      {endpoints.isError && <Alert type="error" showIcon message={errorText(endpoints.error)} />}

      <Card
        size="small"
        title="나가는 연동"
        extra={
          <Space>
            <PageHelp title="외부 연동 — 화면 설명">
              <HelpP>
                정해진 사건이 생기면 등록한 주소로 우리가 JSON 을 보냅니다. 고객사 ERP·그룹웨어·
                메신저가 받아 가도록 할 때 씁니다.
              </HelpP>
              <HelpH>보내는 모양</HelpH>
              <HelpP>
                본문은{' '}
                <Text code>{'{ event, occurredAt, tenantId, data }'}</Text> 입니다. 헤더에는{' '}
                <Text code>X-WORKNHUB-EVENT</Text> 가 붙습니다.
              </HelpP>
              <HelpH>인증 방식</HelpH>
              <HelpList
                items={[
                  { term: 'Bearer 토큰', desc: 'Authorization: Bearer <토큰> 으로 보냅니다.' },
                  { term: '헤더에 키', desc: '지정한 헤더 이름에 값을 담습니다. (예: X-API-Key)' },
                  { term: 'Basic 인증', desc: '입력한 값을 그대로 base64 로 바꿔 보냅니다.' },
                  {
                    term: 'HMAC 서명',
                    desc: '본문을 비밀키로 서명해 X-WORKNHUB-SIGNATURE 에 담습니다. 받는 쪽이 위조를 걸러낼 수 있습니다.',
                  },
                ]}
              />
              <HelpH>실패하면</HelpH>
              <HelpP>
                1분마다 도는 배치가 다시 보냅니다. 시도할수록 간격이 벌어지고, 재시도 상한을 넘기면
                멈춥니다. 상대 서버를 고친 뒤에는 아래 이력에서 「다시 보내기」를 누르세요.
              </HelpP>
              <HelpH>주의</HelpH>
              <HelpP>
                내부망·루프백 주소(localhost, 192.168.x.x 등)로는 보낼 수 없습니다. 이 서버가 내부망을
                들여다보는 통로가 되지 않도록 막아 두었습니다.
              </HelpP>
            </PageHelp>
            <Button icon={<ReloadOutlined />} onClick={() => void refresh()} />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
              연동 등록
            </Button>
          </Space>
        }
      >
        <Table<Endpoint>
          rowKey="endpointId"
          size="middle"
          columns={columns}
          dataSource={endpoints.data ?? []}
          loading={endpoints.isLoading}
          pagination={false}
          scroll={{ x: 1100 }}
          locale={{ emptyText: '등록된 연동이 없습니다.' }}
        />
      </Card>

      <Card
        size="small"
        title="전송 이력"
        extra={
          <Select
            allowClear
            placeholder="연동 전체"
            style={{ width: 220 }}
            value={filterEndpoint}
            onChange={setFilterEndpoint}
            options={(endpoints.data ?? []).map((e) => ({
              value: e.endpointId,
              label: e.endpointName,
            }))}
          />
        }
      >
        <Table<Delivery>
          rowKey="deliveryId"
          size="small"
          loading={deliveries.isLoading}
          dataSource={deliveries.data ?? []}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: '보낸 기록이 없습니다.' }}
          columns={[
            {
              title: '시각',
              dataIndex: 'queuedAt',
              key: 'queuedAt',
              width: 160,
              render: (v: string) => new Date(v).toLocaleString('ko-KR'),
            },
            { title: '연동', dataIndex: 'endpointName', key: 'endpointName', width: 180 },
            {
              title: '사건',
              dataIndex: 'eventCode',
              key: 'eventCode',
              width: 150,
              render: (v: string) => eventLabel(v),
            },
            {
              title: '상태',
              key: 'status',
              width: 150,
              render: (_, row) => {
                const m = STATUS_META[row.status] ?? { text: row.status, status: 'default' as const };
                return (
                  <Space size={6}>
                    <Badge status={m.status} text={m.text} />
                    {row.responseStatus !== null && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        HTTP {row.responseStatus}
                      </Text>
                    )}
                    {row.attempt > 1 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {row.attempt}회
                      </Text>
                    )}
                  </Space>
                );
              },
            },
            {
              title: '내용',
              key: 'detail',
              render: (_, row) => (
                <Text type={row.errorMessage ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                  {row.errorMessage ?? row.responseBody ?? '-'}
                </Text>
              ),
            },
            {
              title: '',
              key: 'retry',
              width: 110,
              render: (_, row) =>
                row.status === 'FAILED' ? (
                  <Button
                    size="small"
                    type="link"
                    onClick={async () => {
                      try {
                        await apiClient.post(
                          `/admin/integrations/deliveries/${row.deliveryId}/retry`,
                        );
                        message.success('다시 줄에 세웠습니다. 1분 안에 보냅니다.');
                        await client.invalidateQueries({
                          queryKey: ['admin', 'integrations', 'deliveries'],
                        });
                      } catch (err) {
                        message.error(errorText(err as NormalizedError));
                      }
                    }}
                  >
                    다시 보내기
                  </Button>
                ) : null,
            },
          ]}
        />
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          보낸 본문은 개인정보가 섞일 수 있어 암호화해 보관하며 화면에 표시하지 않습니다. 상대가
          돌려준 응답과 실패 사유만 보여 줍니다.
        </Paragraph>
      </Card>

      {/* ---- 등록 · 수정 ---------------------------------------------- */}
      <Modal
        open={open}
        title={editing ? `${editing.endpointName} — 수정` : '연동 등록'}
        okText="저장"
        cancelText="취소"
        confirmLoading={save.isPending}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => void submit()}
        width={680}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {formError && (
            <Alert type="error" showIcon message={formError} style={{ marginBottom: 16 }} />
          )}

          <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
            <Form.Item
              name="endpointCode"
              label="연동 코드"
              rules={[{ required: true, message: '코드를 입력하세요' }]}
              extra="영문 대문자·숫자·밑줄"
            >
              <Input placeholder="ERP_WEBHOOK" style={{ width: 220 }} disabled={Boolean(editing)} />
            </Form.Item>
            <Form.Item
              name="endpointName"
              label="이름"
              rules={[{ required: true, message: '이름을 입력하세요' }]}
            >
              <Input placeholder="본사 ERP 수신" style={{ width: 300 }} />
            </Form.Item>
          </Space>

          <Form.Item name="description" label="설명">
            <Input placeholder="무엇을 위한 연동인지 적어 두세요" />
          </Form.Item>

          <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
            <Form.Item
              name="targetUrl"
              label="보낼 주소"
              rules={[{ required: true, message: '주소를 입력하세요' }]}
              extra="내부망·루프백 주소는 쓸 수 없습니다"
            >
              <Input placeholder="https://erp.example.com/hooks/worknhub" style={{ width: 420 }} />
            </Form.Item>
            <Form.Item name="httpMethod" label="메서드">
              <Select
                style={{ width: 110 }}
                options={['POST', 'PUT', 'PATCH'].map((v) => ({ value: v, label: v }))}
              />
            </Form.Item>
          </Space>

          <Form.Item
            name="events"
            label="구독 사건"
            extra="고르지 않으면 아무것도 보내지 않습니다"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="보낼 사건을 고르세요"
              loading={codes.isLoading}
              options={eventItems.map((e) => ({ value: e.itemCode, label: e.itemName }))}
            />
          </Form.Item>

          <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
            <Form.Item name="authType" label="인증 방식">
              <Select
                style={{ width: 160 }}
                options={(Object.keys(AUTH_LABEL) as AuthType[]).map((v) => ({
                  value: v,
                  label: AUTH_LABEL[v],
                }))}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(a, b) => a.authType !== b.authType}
            >
              {({ getFieldValue }) =>
                getFieldValue('authType') === 'HEADER' ? (
                  <Form.Item
                    name="authHeaderName"
                    label="헤더 이름"
                    rules={[{ required: true, message: '헤더 이름을 입력하세요' }]}
                  >
                    <Input placeholder="X-API-Key" style={{ width: 200 }} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(a, b) => a.authType !== b.authType}
            >
              {({ getFieldValue }) =>
                getFieldValue('authType') !== 'NONE' ? (
                  <Form.Item
                    name="secret"
                    label="토큰 · 비밀키"
                    extra={
                      editing?.hasSecret
                        ? '비워 두면 기존 값을 그대로 둡니다'
                        : '저장 뒤에는 다시 볼 수 없습니다'
                    }
                  >
                    <Input.Password
                      placeholder={editing?.hasSecret ? '●●●●●●●● (저장됨)' : ''}
                      style={{ width: 280 }}
                      autoComplete="new-password"
                    />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Space>

          <Space size="middle" wrap align="start" style={{ display: 'flex' }}>
            <Form.Item name="timeoutMs" label="응답 대기(ms)">
              <InputNumber min={1000} max={60000} step={1000} style={{ width: 150 }} />
            </Form.Item>
            <Form.Item name="maxRetry" label="재시도 상한">
              <InputNumber min={0} max={10} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="isActive" label="사용" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
}
